import { useEffect, useRef, useState } from "react";
import Image from "@Components/Image";
import Reveal from "@Components/Reveal/Reveal";
import TitleStamp from "@Components/TitleStamp/TitleStamp";
import styles from "./HowItWorksHero.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import { pageAsset } from "@Utils/getStaticAssetPath";

// Images come from the static-assets bucket, never `import`ed — see `pageAsset`.
const asset = pageAsset("HowItWorksV2");

// ⚠️  `hero-2026.jpg`, NOT `hero.jpg`. The static-assets pipeline skips keys that
// already exist and CloudFront caches them for 100 days, so replacing the file under
// its old name neither uploads nor propagates. A new name does both, and keeps the
// live hero unchanged until this merges. See the same note in CareerV2's Gallery.
const heroImg = asset("hero-2026.jpg");

// Distance (px) over which the hero image eases from full-bleed to contained.
const SHRINK_DISTANCE = 420;

const Hero = () => {
  const { t } = useTranslationHook();
  const [mediaShown, setMediaShown] = useState(false);
  const mediaRef = useRef<HTMLDivElement>(null);

  // On first load: reveal the hero image AFTER the title + subtitle have shown.
  useEffect(() => {
    const timer = window.setTimeout(() => setMediaShown(true), 350);
    return () => window.clearTimeout(timer);
  }, []);

  // Hero image: starts full-bleed (edge-to-edge) and eases to its contained,
  // rounded size as the user scrolls down the first ~420px. Driven by a CSS
  // variable so the interpolation lives in CSS; rAF-throttled for smoothness.
  useEffect(() => {
    const node = mediaRef.current;
    if (!node) return undefined;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      node.style.setProperty("--shrink", "1");
      return undefined;
    }

    let raf = 0;
    const update = () => {
      raf = 0;
      const progress = Math.min(1, Math.max(0, window.scrollY / SHRINK_DISTANCE));
      node.style.setProperty("--shrink", String(progress));
    };
    const onScroll = () => {
      if (!raf) raf = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section className={styles.hero}>
      <div className={styles.header}>
        <Reveal as="h1" className={styles.title}>
          {t("howitworks.v2.hero.title.line1")}
          <br />
          {t("howitworks.v2.hero.title.brand")}
          <TitleStamp />
          {t("howitworks.v2.hero.title.line2")}
        </Reveal>
        <Reveal as="p" className={styles.subtitle} delay={120}>
          {t("howitworks.v2.hero.subtitle")}
        </Reveal>
      </div>

      <div ref={mediaRef} className={`${styles.media} ${mediaShown ? styles.mediaShown : ""}`}>
        <Image
          src={heroImg}
          alt={t("howitworks.v2.hero.imageAlt")}
          className={styles.image}
          width="100%"
          height="100%"
          isEagerLoad
        />
      </div>
    </section>
  );
};

export default wrapperHOC(Hero, {
  componentName: "Hero-HowItWorksV2",
  showForChina: true,
});
